// Louisiana State University: public college page (#2652, round 2), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the headline class is now Fall 2025 from
// LSU's Application Statistics trend report, the class the KB holds;
// residency stays the Fall 2024 Common Data Set's (LSU has posted no newer
// edition).
//  - LSU's Common Data Set 2024-2025, the newest edition LSU posts (the
//    Office of Data and Strategic Analytics' Common Data Set page lists Fall
//    2024 as its latest year; no 2025-2026 files exist), section C as its own
//    PDF (3_2425_admissions.pdf): C1 applicants, admits and enrollees for
//    Fall 2024 with the residency table, C8 test policy (written for fall
//    2026 applicants), C21/C22 (no early decision, no early action).
//  - LSU Undergraduate Admissions: Freshman (deadlines for the Common App
//    "for ALL 2027 ENTRY TERMS", test score rules) and the Test Optional FAQ
//    ("First-year students applying to LSU for any 2027 entry term"). LSU
//    publishes no decision dates.
//  - LSU Financial Aid, Cost: the 2026-2027 cost of attendance (fall 2026
//    and spring 2027) for a student living in a residence hall, item by item.
//    The 2024-2025 CDS gives 2025-2026 costs only.
//  - College Scorecard API, school id 159391 (net price by family income).
// Departures: the counts are Fall 2024, not Fall 2025, because LSU has not
// posted a 2025-2026 CDS. The 2024-2025 CDS C8 marks scores "not required,
// but considered" for fall 2026; LSU's admissions pages for 2027 entry
// require scores from applicants with a weighted GPA below 3.5, and the
// admissions pages win.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'LSU Common Data Set 2024-2025, section C',
        url: 'https://www.lsu.edu/data/common-data-set/2024/3_2425_admissions.pdf',
        read: '2026-10-01',
    },
    freshman: {
        label: 'LSU Undergraduate Admissions, Freshman',
        url: 'https://www.lsu.edu/admissions/apply/freshman.php',
        read: '2026-10-01',
    },
    testFaq: {
        label: 'LSU Undergraduate Admissions, Test Optional FAQ',
        url: 'https://www.lsu.edu/admissions/apply/test-optional-faq.php',
        read: '2026-10-01',
    },
    cost: {
        label: 'LSU Financial Aid, Cost of Attendance 2026-27',
        url: 'https://www.lsu.edu/financialaid/cost/index.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?159391-Louisiana-State-University-and-Agricultural-and-Mechanical-College',
        read: '2026-10-15',
    },
    trend: {
        label: 'LSU Trend Data, Application Statistics',
        url: 'https://www.lsu.edu/data/student-data/trend_report/appstats.pdf',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'louisiana-state-university',
    kbId: 'louisiana_state_university',
    name: 'Louisiana State University',
    shortName: 'LSU',
    aliases: ['LSU', 'Louisiana State'],
    location: 'Baton Rouge, LA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (LSU Athletics media center); decoration only.
    branding: {
        primary: '#461D7C',
        secondary: '#FDD023',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/louisiana_state_university_logo.png',
    },

    // The direct answer and short FAQ (#4229, founder 2026-10-14: content
    // only, no title change). Every figure restates one the page already
    // shows from the source named beside it; CollegePages.test.jsx fails a
    // number the rest of the page does not hold.
    // The search it answers: lsu acceptance rate out of state.
    faq: {
        answer: { text: 'LSU admitted 72.3% of out-of-state first-year applicants for fall 2024, 24,838 of the 34,357 who applied, according to its Common Data Set 2024-2025.', source: 'cds', where: 'section C1, residency table' },
        questions: [
            { q: 'What is the LSU acceptance rate?', a: 'For fall 2025, LSU admitted 37,945 of 54,687 new freshman applicants, 69.4%, and 8,243 enrolled, according to its Application Statistics trend report.', source: 'trend' },
            { q: 'What is the LSU acceptance rate for in-state students?', a: 'For fall 2024, LSU admitted 8,923 of 11,135 Louisiana applicants, 80.1%, against 72.3% for out-of-state applicants.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the LSU application fee?', a: 'The application fee is $50, and eligible students can use a fee waiver. LSU takes first-year applications only through the Common App.', source: 'freshman' },
            { q: 'What are the LSU application deadlines?', a: 'For fall 2027 entry, the priority deadline for scholarships is December 1 and the Regular Decision deadline is February 1. Transcripts and scores can follow the application.', source: 'freshman' },
            { q: 'Does LSU require the SAT or ACT?', a: 'It depends on your GPA. If your cumulative weighted high school GPA is below 3.5, you must send an ACT or SAT score to be considered for admission and merit scholarships. With 3.5 or higher, scores are your choice, and you mark on the Common App whether LSU should consider them.', source: 'testFaq' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: null,
        applied: 54687,
        admitted: 37945,
        enrolled: 8243,
        source: 'trend',
        where: 'Trend Data, Application Statistics, new freshmen, Fall 2025 (updated April 7, 2026)',
        residency: [
            { group: 'In-state', applied: 11135, admitted: 8923 },
            { group: 'Out-of-state', applied: 34357, admitted: 24838 },
            { group: 'International', applied: 1573, admitted: 752 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
        residencyEntering: 'Fall 2024',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'LSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'LSU has no early decision or early action plan; the Common Data Set answers no to both. Its December 1 date is a priority deadline for scholarships, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision deadline', value: 'February 1' },
        rows: [
            { plan: 'Priority deadline for scholarships', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'LSU takes first-year applications only through the Common App, which is also the application for LSU merit scholarships and the Ogden Honors College. The application fee is $50, and eligible students can use a fee waiver. Transcripts and scores can follow the application. LSU does not publish decision dates.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test-optional for some applicants, not all: if your cumulative weighted high school GPA is below 3.5, you must send an ACT or SAT score to be considered for admission and merit scholarships. With a 3.5 or higher, scores are your choice, and you mark on the Common App whether LSU should consider them. LSU superscores, and there is no minimum score.',
        source: 'testFaq',
        cdsLine: 'The 2024-2025 Common Data Set, written for fall 2026 applicants, marks the SAT or ACT as not required but considered if submitted; LSU\'s admissions pages for 2027 entry add the 3.5 GPA rule above.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [12886, 29562] },
            { item: 'Food', amounts: [5584, 5584] },
            { item: 'Housing (residence hall)', amounts: [10300, 10300] },
            { item: 'Books and supplies', amounts: [1020, 1020] },
            { item: 'Transportation', amounts: [3336, 3336] },
            { item: 'Personal', amounts: [6320, 6320] },
        ],
        totals: [39446, 56122],
        note: 'For a student living in a residence hall, fall 2026 and spring 2027. Tuition and fees are LSU\'s average 2026-27 rates at 15 semester hours.',
        source: 'cost',
        netPrice: {
            average: 19286,
            byIncome: [
                { income: '$0 to $30,000', amount: 14670 },
                { income: '$30,001 to $48,000', amount: 15598 },
                { income: '$48,001 to $75,000', amount: 18807 },
                { income: '$75,001 to $110,000', amount: 23421 },
                { income: 'Over $110,000', amount: 27239 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from LSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
